import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import DetailsTab from "./tabs/DetailsTab";
import HistoryTab from "./tabs/HistoryTab";
import OpenItemsTab from "./tabs/OpenItemsTab";
import CashReceiptsTab from "./tabs/CashReceiptsTab";
import TransactionsTab from "./tabs/TransactionsTab";
import ContactTab from "./tabs/ContactTab";
import axios from "axios";
import data from "../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { EMPTY_GUID, ROUTES } from './constants';
import { DEFAULT_TYPE, DEFAULT_STATUS } from './Layout';
import { MESSAGES } from '../../../common/Messages';

// Matches uARM0009.pas's qryARCollectionCreditHeaderEditAfterInsert: every new header
// defaults Type/Status/InitialCallDate this way. A function (not a static object) so
// InitialCallDate is "now" at the moment the record is actually started, not at module load.
function buildInitialFormFields() {
    return {
        ROWID: EMPTY_GUID,
        RECORD: '',
        R_CUSTOMER: null,
        REFERENCE: '',
        DESCRIPTION: '',
        TYPE: DEFAULT_TYPE,
        STATUS: DEFAULT_STATUS,
        INITIALCALLDATE: new Date(),
        COMMENT: '',
    };
}

// Single place that turns a CollectionCreditHeaderDTO into this form's field names, so the
// load-by-id and load-by-customer paths can never map the same payload differently.
function mapHeaderToFormFields(item) {
    return {
        ROWID: item.RowId || EMPTY_GUID,
        RECORD: item.RecordNumber || '',
        R_CUSTOMER: item.RCustomer || null,
        REFERENCE: item.Reference || '',
        DESCRIPTION: item.Description || '',
        TYPE: item.Type || '',
        STATUS: item.Status || '',
        INITIALCALLDATE: item.InitialCallDate || null,
        COMMENT: item.Comment || '',
    };
}

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const [formFields, setformFields] = useState(buildInitialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));
    }, []);

    // Clicking through a browse grid fires one load per row: only the newest may write to
    // state, or a slow earlier response lands on top of the row the user actually picked.
    const loadSequence = useRef(0);

    // Both loads go through ar/CollectionCreditHeader/GetCollectionCreditHeader, which returns
    // one whole header - by its own RowId (the Open Follow-up grid's selection) or by customer
    // (the Customer grid's). Loading the header, rather than just stamping fields, is what
    // makes the sub-tabs reload: ROWID drives the Details grid and R_CUSTOMER drives
    // History/Open Items/Cash Receipts/Transactions/Contact.
    const loadCollectionCreditHeader = useCallback(async ({ rowId, customerId }) => {
        const sequence = ++loadSequence.current;
        const isCurrent = () => sequence === loadSequence.current;

        const hasRowId = !!rowId && rowId !== EMPTY_GUID;
        const hasCustomerId = !!customerId && customerId !== EMPTY_GUID;

        // Nothing selected - a blank record, so the previously selected row's values can't
        // linger on screen (legacy resets iCurrentKey to AB_EMPTYGUID_STR as the grid scrolls).
        if (!hasRowId && !hasCustomerId) {
            setformFields(buildInitialFormFields());
            return;
        }

        // No follow-up for this customer yet - a blank record prefilled with them, matching
        // legacy's empty query plus qryARCollectionCreditHeaderEditAfterInsert defaulting
        // R_CUSTOMER from iCurrentCustomer.
        const blankForCustomer = () => ({ ...buildInitialFormFields(), R_CUSTOMER: customerId || null });

        try {
            setLoadPanelVisible(true);
            const query = hasRowId ? `rowid=${rowId}` : `customerRowId=${customerId}`;
            const url = `${window._env_.REACT_APP_APIS}/${data.CollectionCreditHeader.GetCollectionCreditHeader}?${query}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (!isCurrent()) return;

            setformFields(response.data ? mapHeaderToFormFields(response.data) : blankForCustomer());
        } catch (error) {
            if (!isCurrent()) return;

            // 404 is the "no follow-up yet" answer for a by-customer lookup, not a failure.
            if (!hasRowId && error?.response?.status === 404) {
                setformFields(blankForCustomer());
                return;
            }

            console.error('Error loading Credit/Collections Follow-up:', error);
            notify(MESSAGES.FOLLOW_UP_LOAD_ERROR, 'error', 3000);
        } finally {
            if (isCurrent()) {
                setLoadPanelVisible(false);
            }
        }
    }, [accessToken]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadCollectionCreditHeader({ rowId });
        } else if (props.loadByCustomer) {
            // Parent grid picks a CUSTOMER rather than a follow-up - ARD0009's Customer tab,
            // where pagBottomOpenFollowUpInsetChange opens the header query on
            // "R_Customer = iCurrentCustomer" and only ANDs in "ROWID = iCurrentKey" while the
            // Open Follow-up tab is active. So the customer's whole follow-up loads here.
            loadCollectionCreditHeader({ customerId: props.customerId });
        } else if (props.customerId) {
            // Arrived from the Customer tab's "Start Follow-up" action - prefill the customer.
            setformFields(prev => ({ ...prev, R_CUSTOMER: props.customerId }));
        }
    }, [props.id, props.customerId, props.loadByCustomer, loadCollectionCreditHeader]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);

        try {
            const payload = {
                RowId: formFields.ROWID && formFields.ROWID !== EMPTY_GUID ? formFields.ROWID : EMPTY_GUID,
                KeySequence: 0,
                RCustomer: formFields.R_CUSTOMER || null,
                Reference: formFields.REFERENCE || "",
                Description: formFields.DESCRIPTION || "",
                Type: formFields.TYPE || "",
                Status: formFields.STATUS || "",
                InitialCallDate: formFields.INITIALCALLDATE || null,
                Comment: formFields.COMMENT || "",
            };

            const isUpdate = formFields.ROWID && formFields.ROWID !== EMPTY_GUID;
            const endpoint = isUpdate ? data.CollectionCreditHeader.UpdateCollectionCreditHeader : data.CollectionCreditHeader.InsertCollectionCreditHeader;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.RowId || prev.ROWID,
                    RECORD: saved.RecordNumber || prev.RECORD,
                    R_CUSTOMER: saved.RCustomer || prev.R_CUSTOMER,
                    REFERENCE: saved.Reference ?? prev.REFERENCE,
                    DESCRIPTION: saved.Description ?? prev.DESCRIPTION,
                    TYPE: saved.Type ?? prev.TYPE,
                    STATUS: saved.Status ?? prev.STATUS,
                    INITIALCALLDATE: saved.InitialCallDate ?? prev.INITIALCALLDATE,
                    COMMENT: saved.Comment ?? prev.COMMENT,
                }));

                // Embedded in a popup (e.g. from OM's Query Orders By Customer): stay put and let
                // the caller's own state track the saved row, instead of navigating the browser to
                // this form's own dedicated route, which would break out of whatever screen opened it.
                if (!isUpdate && saved.RowId && !props.isPopup) {
                    history.replace(`${ROUTES.EDIT}?id=${saved.RowId}`);
                }
            }

            notify(MESSAGES.FOLLOW_UP_SAVE_SUCCESS, "success", 3000);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                err?.message ||
                MESSAGES.FOLLOW_UP_SAVE_ERROR;

            notify(message, "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const DeleteClick = () => {
        if (!formFields.ROWID || formFields.ROWID === EMPTY_GUID) {
            notify(MESSAGES.NO_ROW_SELECTED, "warning", 3000);
            return;
        }

        // Deliberately not the standard "Delete selected record?" wording: this is the
        // header-level cascade (it also removes the follow-up details), matching ARD0009.
        let result = confirm(`<i>${MESSAGES.FOLLOW_UP_DELETE_HEADER_CONFIRM}</i>`, MESSAGES.DELETE_CONFIRMATION);
        result.then(async (dialogResult) => {
            if (dialogResult) {
                setLoadPanelVisible(true);
                try {
                    const url = `${window._env_.REACT_APP_APIS}/${data.CollectionCreditHeader.DeleteCollectionCreditHeader}`;
                    const response = await axios.post(url, formFields.ROWID, {
                        headers: {
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    const deleteSucceeded = response.data && (response.data.Success ?? response.data.success);
                    if (deleteSucceeded) {
                        notify(MESSAGES.FOLLOW_UP_DELETE_SUCCESS, "success", 3000);
                        setformFields(buildInitialFormFields());
                        // Embedded in a popup: don't navigate the browser to this form's own list
                        // route - resetting to a blank record (above) is enough to leave it usable.
                        if (!props.isPopup) {
                            setTimeout(() => {
                                history.push(ROUTES.LIST);
                            }, 1000);
                        }
                    } else {
                        notify(response.data?.message || MESSAGES.FOLLOW_UP_DELETE_ERROR, "error", 5000);
                    }
                } catch (error) {
                    const message = error?.response?.data?.message || error?.message || MESSAGES.FOLLOW_UP_DELETE_ERROR;
                    notify(message, "error", 5000);
                } finally {
                    hideLoadPanel();
                }
            }
        });
    };

    const addNewRecordClick = () => {
        setformFields(buildInitialFormFields());
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadCollectionCreditHeader({ rowId });
        } else if (props.loadByCustomer) {
            // Customer-driven panel: reset back to the customer's own follow-up, not to a
            // blank record for nobody.
            loadCollectionCreditHeader({ customerId: props.customerId });
        } else {
            setformFields(buildInitialFormFields());
        }
    };

    const goBack = () => {
        history.push(ROUTES.LIST);
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6 style={{ color: window._env_.Color2, marginLeft: '12px' }}>
                                            {formFields.REFERENCE ? `AR Credit/Collections Follow-up - ${formFields.REFERENCE}` : 'AR Credit/Collections Follow-up'}
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button text="Save" useSubmitBehavior={true} type="default" className="accordianButton" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={DeleteClick} text="Delete" disabled={formFields.ROWID === EMPTY_GUID} type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                {!props.isPopup && (
                                    <Item location="after">
                                        <Button className="accordianButton" onClick={goBack} text="Back" />
                                    </Item>
                                )}
                            </Toolbar>
                            {/* Fixed (not props.id-dependent): this form can now be embedded somewhere
                                that changes props.id on an already-mounted instance (e.g. picking a
                                different row in a browse grid) instead of always receiving a fixed id
                                from the route. Toggling deferRendering on a live TabPanel destroys/
                                recreates tab content out from under still-mounted child components
                                (e.g. PrimaryTab's DevExtreme refs going stale), throwing "Cannot read
                                properties of undefined (reading 'option')". A constant value avoids that
                                and is the more standard TabPanel setting anyway. */}
                            <TabPanel deferRendering={true} showNavButtons={true} height={"auto"}>
                                <TabItem title="Followup">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Details">
                                    <DetailsTab
                                        headerId={formFields.ROWID}
                                        headerReference={formFields.REFERENCE}
                                        customerId={formFields.R_CUSTOMER}
                                    />
                                </TabItem>
                                <TabItem title="Collections History">
                                    <HistoryTab customerId={formFields.R_CUSTOMER} />
                                </TabItem>
                                <TabItem title="Open Items">
                                    <OpenItemsTab customerId={formFields.R_CUSTOMER} />
                                </TabItem>
                                <TabItem title="Cash Receipts">
                                    <CashReceiptsTab customerId={formFields.R_CUSTOMER} />
                                </TabItem>
                                <TabItem title="Transactions">
                                    <TransactionsTab customerId={formFields.R_CUSTOMER} />
                                </TabItem>
                                <TabItem title="Contact">
                                    <ContactTab customerId={formFields.R_CUSTOMER} />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
